'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiFetch, apiFetchWithRetry } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { Pencil, Trash2, ListTodo, Loader2, Calendar, CheckCircle2, AlertTriangle, Activity, ClipboardList } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { DateInput } from '@/components/ui/date-input'
import { formatDate } from '@/lib/utils'
import { useMultiOperations } from '@/hooks/useOperations'
import { PageShell } from '@/components/ui/page-shell'
import { SectionHeader } from '@/components/ui/section-header'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'
import { cn } from '@/lib/utils'

interface User {
  id: string
  name: string
  email: string
  employeeId: string | null
}

interface Project {
  id: string
  name: string
}

interface Task {
  id: string
  title: string
  description: string | null
  status: string
  priority: string
  dueDate: string | null
  projectId: string | null
  project: Project | null
  assignees: User[]
}

type GlassTone = 'default' | 'orange' | 'green' | 'blue' | 'red' | 'amber' | 'violet' | 'cyan'
function toGlassTone(t: ReturnType<typeof statusTone>): GlassTone {
  return t === 'neutral' ? 'default' : t
}

function priorityTone(priority: string): 'red' | 'orange' | 'amber' | 'neutral' {
  switch (priority) {
    case 'CRITICAL': return 'red'
    case 'HIGH': return 'orange'
    case 'MEDIUM': return 'amber'
    default: return 'neutral'
  }
}

const STATUS_FILTERS: Array<{ key: string; label: string; tone: 'default' | 'amber' | 'blue' | 'green' | 'red' }> = [
  { key: 'ALL', label: 'All', tone: 'default' },
  { key: 'PENDING', label: 'Pending', tone: 'amber' },
  { key: 'IN_PROGRESS', label: 'In Progress', tone: 'blue' },
  { key: 'COMPLETED', label: 'Completed', tone: 'green' },
  { key: 'CANCELLED', label: 'Cancelled', tone: 'red' },
]

const PRIORITY_FILTERS: Array<{ key: string; label: string; tone: 'default' | 'red' | 'orange' | 'amber' | 'neutral' }> = [
  { key: 'ALL', label: 'All', tone: 'default' },
  { key: 'CRITICAL', label: 'Critical', tone: 'red' },
  { key: 'HIGH', label: 'High', tone: 'orange' },
  { key: 'MEDIUM', label: 'Medium', tone: 'amber' },
  { key: 'LOW', label: 'Low', tone: 'neutral' },
]

export function TaskManagement() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editTask, setEditTask] = useState<Task | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [priorityFilter, setPriorityFilter] = useState<string>('ALL')
  const [formData, setFormData] = useState<{
    title: string
    description: string
    status: string
    priority: string
    dueDate: string
    projectId: string
    assignedTo: string[]
  }>({
    title: '',
    description: '',
    status: 'PENDING',
    priority: 'MEDIUM',
    dueDate: '',
    projectId: 'none',
    assignedTo: []
  })
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const { states, execute } = useMultiOperations()

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [tasksResult, usersResult, projectsResult] = await Promise.allSettled([
        apiFetchWithRetry('/api/tasks').then(r => r.json()),
        apiFetchWithRetry('/api/users').then(r => r.json()),
        apiFetchWithRetry('/api/projects').then(r => r.json())
      ])

      if (tasksResult.status === 'fulfilled') {
        const tasksData = tasksResult.value
        if (tasksData.tasks && Array.isArray(tasksData.tasks)) {
          setTasks(tasksData.tasks)
        }
      } else {
        console.error('Error fetching tasks:', tasksResult.reason)
        toast.error(tasksResult.reason?.message || 'Failed to fetch tasks')
      }

      if (usersResult.status === 'fulfilled') {
        const usersData = usersResult.value
        if (usersData.users && Array.isArray(usersData.users)) {
          const showInactive = localStorage.getItem('sasha_show_inactive_users') === 'true'
          setUsers(usersData.users.filter((u: any) => u && u.role === 'EMPLOYEE' && (showInactive || u.isActive !== false)))
        }
      } else {
        console.error('Error fetching users:', usersResult.reason)
        toast.error(usersResult.reason?.message || 'Failed to fetch users')
      }

      if (projectsResult.status === 'fulfilled') {
        const projectsData = projectsResult.value
        if (projectsData.projects && Array.isArray(projectsData.projects)) {
          setProjects(projectsData.projects)
        }
      } else {
        console.error('Error fetching projects:', projectsResult.reason)
        toast.error(projectsResult.reason?.message || 'Failed to fetch projects')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const stats = useMemo(() => {
    const total = tasks.length
    const inProgress = tasks.filter(t => t.status === 'IN_PROGRESS').length
    const completed = tasks.filter(t => t.status === 'COMPLETED').length
    const now = Date.now()
    const overdue = tasks.filter(t => {
      if (!t.dueDate || t.status === 'COMPLETED' || t.status === 'CANCELLED') return false
      return new Date(t.dueDate).getTime() < now
    }).length
    return { total, inProgress, completed, overdue }
  }, [tasks])

  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      if (statusFilter !== 'ALL' && t.status !== statusFilter) return false
      if (priorityFilter !== 'ALL' && t.priority !== priorityFilter) return false
      return true
    })
  }, [tasks, statusFilter, priorityFilter])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (formData.assignedTo.length === 0) {
      toast.error('Select at least one assignee')
      return
    }

    execute('saveTask', async () => {
      const url = editTask ? `/api/tasks/${editTask.id}` : '/api/tasks'
      const method = editTask ? 'PUT' : 'POST'

      await apiFetchWithRetry(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          projectId: formData.projectId === 'none' ? null : formData.projectId
        })
      })

      toast.success(editTask ? 'Task updated successfully' : 'Task created successfully')
      fetchData()
      setDialogOpen(false)
      resetForm()
    })
  }

  const handleDelete = async (taskId: string) => {
    if (!confirm('Are you sure you want to delete this task?')) return

    execute('deleteTask', async () => {
      await apiFetch(`/api/tasks/${taskId}`, { method: 'DELETE' })
      toast.success('Task deleted successfully')
      fetchData()
    })
  }

  const openEditDialog = (task: Task) => {
    setEditTask(task)
    setFormData({
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '',
      projectId: task.projectId || 'none',
      assignedTo: task.assignees.map(u => u.id)
    })
    setDialogOpen(true)
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      status: 'PENDING',
      priority: 'MEDIUM',
      dueDate: '',
      projectId: 'none',
      assignedTo: []
    })
    setEditTask(null)
  }

  const toggleAssignee = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      assignedTo: prev.assignedTo.includes(userId)
        ? prev.assignedTo.filter(id => id !== userId)
        : [...prev.assignedTo, userId]
    }))
  }

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    execute('statusChange', async () => {
      await apiFetch(`/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      })

      toast.success('Task status updated')
      fetchData()
    })
  }

  const addButton = (
    <Dialog open={dialogOpen} onOpenChange={(open) => {
      setDialogOpen(open)
      if (!open) resetForm()
    }}>
      <DialogTrigger asChild>
        <Button className={PRIMARY_BTN}>
          <ListTodo className="mr-2 h-4 w-4" />
          New Task
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-white/95 dark:bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-neutral-200 dark:border-white/10">
        <DialogHeader>
          <DialogTitle className="text-neutral-900 dark:text-white">{editTask ? 'Edit Task' : 'Create New Task'}</DialogTitle>
          <DialogDescription className="text-neutral-600 dark:text-white/60">
            {editTask ? 'Update task details' : 'Create a new task and assign it to an employee'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title" className="text-neutral-800 dark:text-white/80">Task Title</Label>
            <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description" className="text-neutral-800 dark:text-white/80">Description</Label>
            <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-neutral-800 dark:text-white/80">Assign To</Label>
              <ScrollArea className="h-32 border border-neutral-200 dark:border-white/10 rounded-xl p-2 bg-neutral-50/50 dark:bg-white/[0.02]">
                <div className="grid grid-cols-1 gap-2">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`user-${user.id}`}
                        checked={formData.assignedTo.includes(user.id)}
                        onChange={() => toggleAssignee(user.id)}
                        className="h-4 w-4 rounded border-neutral-200 dark:border-white/20 bg-neutral-900 text-orange-600 focus:ring-orange-600"
                      />
                      <label htmlFor={`user-${user.id}`} className="text-sm text-neutral-800 dark:text-white/80 cursor-pointer">
                        {user.name}
                      </label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-800 dark:text-white/80">Project (Optional)</Label>
              <Select value={formData.projectId} onValueChange={(value) => setFormData({ ...formData, projectId: value })}>
                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Project</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-800 dark:text-white/80">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-800 dark:text-white/80">Priority</Label>
              <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <Label htmlFor="dueDate" className="text-neutral-800 dark:text-white/80">Due Date</Label>
              <DateInput
                id="dueDate"
                value={formData.dueDate}
                onChange={(date) => setFormData({ ...formData, dueDate: date })}
                label="Due Date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={states.saveTask?.loading} className={PRIMARY_BTN}>
              {states.saveTask?.loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {editTask ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editTask ? 'Update Task' : 'Create Task'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )

  return (
    <PageShell>
      <SectionHeader
        title="Task Management"
        description="Assign and manage daily tasks"
        icon={ClipboardList}
        action={addButton}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        <KpiTile label="Total Tasks" value={stats.total} icon={ClipboardList} tone="orange" />
        <KpiTile label="In Progress" value={stats.inProgress} icon={Activity} tone="blue" />
        <KpiTile label="Completed" value={stats.completed} icon={CheckCircle2} tone="green" />
        <KpiTile label="Overdue" value={stats.overdue} icon={AlertTriangle} tone="red" />
      </div>

      {/* Filter pills */}
      <GlassCard inset className="p-3 md:p-4">
        <div className="space-y-3">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wider text-neutral-400 dark:text-white/40 mb-2">Status</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map(f => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setStatusFilter(f.key)}
                  className={cn(
                    'transition-all',
                    statusFilter === f.key ? 'scale-105 ring-2 ring-white/20 rounded-full' : 'opacity-70 hover:opacity-100'
                  )}
                >
                  <StatusPill tone={f.tone}>{f.label}</StatusPill>
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wider text-neutral-400 dark:text-white/40 mb-2">Priority</p>
            <div className="flex flex-wrap gap-2">
              {PRIORITY_FILTERS.map(f => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setPriorityFilter(f.key)}
                  className={cn(
                    'transition-all',
                    priorityFilter === f.key ? 'scale-105 ring-2 ring-white/20 rounded-full' : 'opacity-70 hover:opacity-100'
                  )}
                >
                  <StatusPill tone={f.tone}>{f.label}</StatusPill>
                </button>
              ))}
            </div>
          </div>
        </div>
      </GlassCard>

      {loading ? (
        <GlassCard>
          <TabLoader />
        </GlassCard>
      ) : filteredTasks.length === 0 ? (
        <GlassCard>
          <EmptyState
            icon={ListTodo}
            title="No tasks match the current filters"
            description="Reset the status or priority pills above, or create a new task."
          />
        </GlassCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
          {filteredTasks.map((task) => {
            const isExpanded = expandedId === task.id
            const isOverdue = task.dueDate
              && task.status !== 'COMPLETED'
              && task.status !== 'CANCELLED'
              && new Date(task.dueDate).getTime() < Date.now()
            return (
              <GlassCard
                key={task.id}
                tone={isOverdue ? 'red' : toGlassTone(statusTone(task.status))}
                interactive
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => openEditDialog(task)}
                    className="text-left min-w-0 flex-1 group"
                  >
                    <p className="text-neutral-900 dark:text-white/90 font-medium truncate group-hover:text-neutral-900 dark:group-hover:text-white">{task.title}</p>
                    {task.project?.name && (
                      <p className="text-[11px] text-neutral-400 dark:text-white/40 truncate">{task.project.name}</p>
                    )}
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(task)} className="text-neutral-600 dark:text-white/60 hover:text-neutral-900 dark:hover:text-white h-8 w-8">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(task.id)}
                      className="text-rose-600 dark:text-rose-300 hover:text-rose-700 dark:hover:text-rose-200 h-8 w-8"
                      disabled={states.deleteTask?.loading}
                    >
                      {states.deleteTask?.loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {task.description && (
                  <div className="mb-3">
                    <p className={cn('text-xs text-neutral-600 dark:text-white/60', isExpanded ? 'whitespace-pre-wrap' : 'line-clamp-2')}>
                      {task.description}
                    </p>
                    {task.description.length > 80 && (
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : task.id)}
                        className="text-[11px] text-orange-600 dark:text-orange-300 hover:text-orange-700 dark:hover:text-orange-200 mt-1"
                      >
                        {isExpanded ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <StatusPill tone={priorityTone(task.priority)}>{task.priority}</StatusPill>
                  {isOverdue && <StatusPill tone="red" dot>Overdue</StatusPill>}
                </div>

                <div className="space-y-2 mb-3">
                  <Select
                    value={task.status}
                    onValueChange={(value) => handleStatusChange(task.id, value)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PENDING">Pending</SelectItem>
                      <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                      <SelectItem value="COMPLETED">Completed</SelectItem>
                      <SelectItem value="CANCELLED">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between gap-2 pt-2 border-t border-neutral-100 dark:border-white/5">
                  <div className="flex -space-x-2 min-w-0">
                    {task.assignees?.slice(0, 3).map((a) => (
                      <div
                        key={a.id}
                        title={a.name}
                        className="h-7 w-7 rounded-full ring-2 ring-neutral-950 bg-gradient-to-br from-orange-500/40 to-violet-500/40 flex items-center justify-center text-[11px] font-medium text-white"
                      >
                        {(a.name || 'U').charAt(0).toUpperCase()}
                      </div>
                    ))}
                    {task.assignees && task.assignees.length > 3 && (
                      <div className="h-7 w-7 rounded-full ring-2 ring-neutral-950 bg-white/10 flex items-center justify-center text-[10px] text-neutral-700 dark:text-white/70">
                        +{task.assignees.length - 3}
                      </div>
                    )}
                    {(!task.assignees || task.assignees.length === 0) && (
                      <span className="text-xs text-neutral-400 dark:text-white/40">Unassigned</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-neutral-500 dark:text-white/50 shrink-0">
                    <Calendar className="h-3.5 w-3.5" />
                    {task.dueDate ? formatDate(task.dueDate) : '—'}
                  </div>
                </div>
              </GlassCard>
            )
          })}
        </div>
      )}
    </PageShell>
  )
}
